import { Transition } from "@headlessui/react";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { XIcon } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import useShop from "@babackapp/private-api-client/client/use-shop";
import { useParams } from "react-router-dom";
import {
  useGetCustomerCarrierPortalLink
} from "@babackapp/private-api-client/client/use-get-customer-carrier-portal-link";
import {
  generatePaymentMethodAdditionLinkQuery
} from "@babackapp/private-api-client/client/use-generate-payment-method-addition-link-query";
import useTranslation from "@babackapp/translations/use-translation";
import { useLocale } from "@babackapp/translations/use-set-locale";

const DISMISS_DURATION_MS = 15 * 60 * 1000;

function dismissBanner(key: string) {
  localStorage.setItem(key, Date.now().toString());
}

/** Schedules `setOpen(true)` respecting a localStorage dismiss cooldown.
 *  Returns a cleanup function to cancel any pending timer. */
function scheduleBanner(key: string, setOpen: (v: boolean) => void) {
  const raw = localStorage.getItem(key);
  const delay = raw
    ? Math.max(0, DISMISS_DURATION_MS - (Date.now() - parseInt(raw)))
    : 1000;
  const timer = setTimeout(() => setOpen(true), delay);
  return () => clearTimeout(timer);
}

export default function BillingWarningCarrierSetupPopup() {
  const params = useParams<{ shopId: string }>();
  const shopId = params.shopId ?? "";
  const { data: shop } = useShop(shopId);
  const t = useTranslation();
  const [locale] = useLocale();
  const [isCarrierOpen, setCarrierIsOpen] = useState(false);
  const [isPaymentOpen, setPaymentIsOpen] = useState(false);

  // To display payment banner, change these conditions
  const isCarrierWarning = false; //!!shop?.billing.stripe.warningCarrierStripeAccount;
  const isPaymentMissing = false; //shop && !shop.billing.stripe?.paymentMethodId;
  // Rendered on every page, `/` included (no shopId): fetched only when the
  // banner can show.
  const { data: carrierPortalLink } = useGetCustomerCarrierPortalLink(
    shopId,
    isCarrierWarning,
  );
  const shouldShow = isCarrierWarning || isPaymentMissing;

  const timeLimit = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(2026, 3, 10));

  useEffect(() => {
    if (isCarrierWarning)
      return scheduleBanner(
        "billing-carrier-banner-dismissed",
        setCarrierIsOpen,
      );
  }, [isCarrierWarning]);

  useEffect(() => {
    if (isPaymentMissing)
      return scheduleBanner(
        "billing-payment-banner-dismissed",
        setPaymentIsOpen,
      );
  }, [isPaymentMissing]);

  const handleCarrierClick = () => {
    if (carrierPortalLink) {
      window.open(carrierPortalLink, "_blank");
    }
  };

  const handlePaymentClick = async () => {
    const { url } = await generatePaymentMethodAdditionLinkQuery({ shopId });
    window.open(url, "_blank");
  };

  if (!shouldShow) return null;

  return (
    <>
      {isCarrierWarning && (
        <Transition
          show={isCarrierOpen}
          as={Fragment}
          enter="transition ease-in-out duration-250"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition ease-in-out duration-250"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="bg-red-600 text-white fixed top-0 left-0 right-0 h-8 z-90 flex flex-row justify-center items-center space-x-4 px-4">
            <div className="flex-1 flex flex-row justify-center items-center space-x-4">
              <div className="text-center text-sm leading-tight">
                <span className="font-semibold">
                  {t("settings.billing.billingAlertAction")}{" "}
                  <span className="underline">
                    {t("settings.billing.billingAlertTransport")}
                  </span>{" "}
                  :
                </span>{" "}
                {t("settings.billing.billingAlertSepa")}{" "}
                <span className="font-bold">{timeLimit}</span>.
              </div>
              <button
                type="button"
                className="inline-flex cursor-pointer justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-0.5 bg-white font-medium text-red-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 text-sm space-x-2 transition-colors shrink-0"
                onClick={handleCarrierClick}
              >
                <div>Ajouter mon SEPA</div>
                <ArrowRightIcon className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => {
                dismissBanner("billing-carrier-banner-dismissed");
                setCarrierIsOpen(false);
              }}
              className="hover:bg-white/20 rounded p-1 transition-colors"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>
        </Transition>
      )}
      {isPaymentMissing && (
        <Transition
          show={isPaymentOpen}
          as={Fragment}
          enter="transition ease-in-out duration-250"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition ease-in-out duration-250"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={`bg-red-600 text-white fixed left-0 right-0 h-8 z-90 flex flex-row justify-center items-center space-x-4 px-4 ${
              isCarrierWarning && isCarrierOpen ? "top-9" : "top-0"
            }`}
          >
            <div className="flex-1 flex flex-row justify-center items-center space-x-4">
              <div className="text-center text-sm leading-tight">
                <span className="font-semibold">
                  {t("settings.billing.billingAlertAction")}{" "}
                  <span className="underline">
                    {t("settings.billing.billingAlertAccount")}
                  </span>{" "}
                  :
                </span>{" "}
                {t("settings.billing.billingAlertPaymentMethod")}{" "}
                <span className="font-bold">{timeLimit}</span>.
              </div>
              <button
                type="button"
                className="inline-flex cursor-pointer justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-0.5 bg-white font-medium text-red-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 text-sm space-x-2 transition-colors shrink-0"
                onClick={handlePaymentClick}
              >
                <div>{t("settings.billing.updatePayementMethod")}</div>
                <ArrowRightIcon className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => {
                dismissBanner("billing-payment-banner-dismissed");
                setPaymentIsOpen(false);
              }}
              className="hover:bg-white/20 rounded p-1 transition-colors"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>
        </Transition>
      )}
    </>
  );
}
